import { createContext, useCallback, useContext, useState } from 'react';

interface SidebarContextValue {
  maxOpenDepth: number;
  prevMaxOpenDepth: number;
  notifyMount: (depth: number) => void;
  notifyUnmount: (depth: number) => void;
  collapse: (depth: number) => void;
  expand: (depth: number) => void;
}

const SidebarContext = createContext<SidebarContextValue>({
  maxOpenDepth: 0,
  prevMaxOpenDepth: 0,
  notifyMount: () => {},
  notifyUnmount: () => {},
  collapse: () => {},
  expand: () => {},
});

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [maxOpenDepth, setMaxOpenDepth] = useState(0);
  const [prevMaxOpenDepth, setPrevMaxOpenDepth] = useState(0);

  const notifyMount = useCallback((depth: number) => {
    setMaxOpenDepth((prev) => Math.max(prev, depth));
  }, []);

  const notifyUnmount = useCallback((depth: number) => {
    setMaxOpenDepth((prev) => (prev >= depth ? depth - 1 : prev));
  }, []);

  const collapse = useCallback((_depth: number) => {
    setMaxOpenDepth((prev) => {
      setPrevMaxOpenDepth(prev);
      return 0;
    });
  }, []);

  const expand = useCallback((depth: number) => {
    setMaxOpenDepth(depth);
  }, []);

  return (
    <SidebarContext.Provider value={{ maxOpenDepth, prevMaxOpenDepth, notifyMount, notifyUnmount, collapse, expand }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useNav() {
  return useContext(SidebarContext);
}
